import { Component, ElementRef, ViewChild, inject, OnDestroy, AfterViewInit, computed } from '@angular/core';
import { Modal, ModalInterface } from 'flowbite';
import { ButtonSecondaryComponent } from '../button-secondary/button-secondary.component';
import { ButtonPrimaryComponent } from '../button-primary/button-primary.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { CommonModule } from '@angular/common';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { ModalService } from '../../services/modal.service';
import { Subscription } from 'rxjs';
import { ModalDialogComponent } from '../modal-dialog/modal-dialog.component';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [
    ButtonSecondaryComponent,
    ButtonPrimaryComponent,
    SvgIconComponent,
    CommonModule,
    TooltipComponent,
    ModalDialogComponent
  ],
  templateUrl: './modal.component.html'
})
export class ModalComponent implements OnDestroy, AfterViewInit {
  public modalService = inject(ModalService);
  public modal: ModalInterface | undefined;
  private openedSubscription: Subscription | undefined;
  private closedSubscription: Subscription | undefined;

  @ViewChild('modalTemplate') private modalRef: ElementRef | undefined;

  public dialogConfig = computed(() => {
    const config = this.modalService.modalConfig();
    return config?.type === 'dialog' ? config : null;
  });

  public fullscreenConfig = computed(() => {
    const config = this.modalService.modalConfig();
    return config?.type === 'fullscreen' ? config : null;
  });

  /** Computed property that returns the appropriate CSS classes based on modal type */
  public modalClass = computed(() => {
    return this.fullscreenConfig()?.type === 'fullscreen'
      ? 'fixed left-0 right-0 top-0 z-50 hidden h-screen w-screen bg-system-backdrop-fill'
      : 'fixed bottom-0 left-0 right-0 top-0 z-100 hidden h-screen w-screen bg-system-backdrop-fill';
  });

  constructor() {
    this.openedSubscription = this.modalService.opened$.subscribe(() => this.open());
    this.closedSubscription = this.modalService.closed$.subscribe(() => this.close());
  }

  ngAfterViewInit() {
    const $targetEl = this.modalRef!.nativeElement;
    this.modal = new Modal($targetEl);
  }

  /** Shows the modal */
  private open() {
    if (this.modal) {
      this.modal.show();
    }
  }

  /** Hides the modal */
  private close() {
    if (this.modal) {
      this.modal.hide();
    }
  }

  /** Handles closing the fullscreen modal */
  public onCloseFullscreen() {
    this.modalService.close();
  }

  /** Handles closing the dialog modal */
  public onCloseDialog() {
    this.modalService.close();
  }

  /**
   * Handles confirming the dialog action
   * Executes the onConfirm callback if provided and closes the modal
   */
  public onConfirmDialog() {
    const config = this.dialogConfig();
    if (config?.onConfirm) {
      config.onConfirm();
    }
    this.modalService.close();
  }

  /**
   * Handles canceling the dialog action
   * Executes the onCancel callback if provided and closes the modal
   */
  public onCancelDialog() {
    const config = this.dialogConfig();
    if (config?.onCancel) {
      config.onCancel();
    }
    this.modalService.close();
  }

  ngOnDestroy() {
    this.openedSubscription?.unsubscribe();
    this.closedSubscription?.unsubscribe();
  }
}
